import { FiCheck, FiRotateCcw, FiTag, FiUsers } from "react-icons/fi";
import type {
  MealDepartmentFilter,
  MealMonitoringDetailStatus,
  MealStatusFilter,
} from "../../../../../api/meal/types/mealMonitoringDetailType";

interface MealDetailFilterPopupProps {
  open: boolean;
  departments: MealDepartmentFilter[];
  statuses: MealStatusFilter[];
  selectedDepartments: number[];
  selectedStatuses: MealMonitoringDetailStatus[];
  onDepartmentChange: (ids: number[]) => void;
  onStatusChange: (statuses: MealMonitoringDetailStatus[]) => void;
  onReset: () => void;
  onApply: () => void;
  onClose: () => void;
}

const MealDetailFilterPopup = ({
  open,
  departments,
  statuses,
  selectedDepartments,
  selectedStatuses,
  onDepartmentChange,
  onStatusChange,
  onReset,
  onApply,
  onClose,
}: MealDetailFilterPopupProps) => {
  if (!open) {
    return null;
  }

  const toggleDepartment = (id: number) => {
    if (selectedDepartments.includes(id)) {
      onDepartmentChange(selectedDepartments.filter((value) => value !== id));
      return;
    }

    onDepartmentChange([...selectedDepartments, id]);
  };

  const toggleStatus = (status: MealMonitoringDetailStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusChange(selectedStatuses.filter((value) => value !== status));
      return;
    }

    onStatusChange([...selectedStatuses, status]);
  };

  return (
    <div className="fixed inset-0 z-[100]">
      <button
        type="button"
        aria-label="Close filter"
        onClick={onClose}
        className="absolute inset-0 bg-black/20"
      />

      <div className="absolute bottom-0 left-0 right-0 rounded-t-[18px] bg-white px-6 pb-6 pt-6">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h2 className="text-[14px] font-bold text-[#555]">Filter</h2>

          {(selectedDepartments.length > 0 || selectedStatuses.length > 0) && (
            <span className="text-[11px] font-medium text-[#ff706b]">
              {selectedDepartments.length + selectedStatuses.length} dipilih
            </span>
          )}
        </div>

        <div className="pt-4">
          <p className="mb-3 flex items-center gap-1.5 text-[12px] font-semibold text-[#555]">
            <FiUsers className="text-[13px] text-[#a7aac0]" />
            Departments
          </p>

          <div className="grid grid-cols-2 gap-2">
            {departments.map((department) => {
              const selected = selectedDepartments.includes(department.id);

              return (
                <button
                  key={department.id}
                  type="button"
                  onClick={() => toggleDepartment(department.id)}
                  className={`flex h-8 items-center justify-center gap-1 rounded-lg border px-2 text-[11px] font-medium transition ${
                    selected
                      ? "border-[#ff706b] bg-white text-[#ff706b]"
                      : "border-transparent bg-[#f2f2f2] text-[#666]"
                  }`}
                >
                  {selected && <FiCheck className="shrink-0 text-[12px]" />}
                  <span className="block truncate">{department.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="pt-5">
          <p className="mb-3 flex items-center gap-1.5 text-[12px] font-semibold text-[#555]">
            <FiTag className="text-[13px] text-[#a7aac0]" />
            Status Makanan
          </p>

          <div className="grid grid-cols-2 gap-2">
            {statuses.map((status) => {
              const statusValue: MealMonitoringDetailStatus | null =
                status.status === "Ordered" || status.status === "Finished"
                  ? status.status
                  : null;

              const selected =
                statusValue !== null && selectedStatuses.includes(statusValue);

              return (
                <button
                  key={status.status}
                  type="button"
                  disabled={statusValue === null}
                  onClick={() => {
                    if (statusValue === null) {
                      return;
                    }

                    toggleStatus(statusValue);
                  }}
                  className={`flex h-8 items-center justify-center gap-1 rounded-lg border px-2 text-[11px] font-medium transition ${
                    selected
                      ? "border-[#ff706b] bg-white text-[#ff706b]"
                      : "border-transparent bg-[#f2f2f2] text-[#666]"
                  } ${
                    statusValue === null ? "cursor-not-allowed opacity-50" : ""
                  }`}
                >
                  {selected && <FiCheck className="shrink-0 text-[12px]" />}
                  <span className="block truncate">{status.description}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onReset}
            className="flex h-10 items-center justify-center gap-1.5 rounded-lg border border-[#ff706b] bg-white text-[12px] font-semibold text-[#ff706b]"
          >
            <FiRotateCcw className="text-[13px]" />
            Reset
          </button>

          <button
            type="button"
            onClick={onApply}
            className="flex h-10 items-center justify-center gap-1.5 rounded-lg bg-[#ff706b] text-[12px] font-semibold text-white"
          >
            <FiCheck className="text-[14px]" />
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default MealDetailFilterPopup;
